import React from 'react';
import Table from "@amzn/awsui-components-react/polaris/table";
import TextFilter from "@amzn/awsui-components-react/polaris/text-filter";
import CollectionPreferences from "@amzn/awsui-components-react/polaris/collection-preferences";
import PatchLevelBadge from '../Common/PatchLevelBadge';
import ExternalLink from '../Common/ExternalLink';
import { ISSUE_URLS } from '../Common/ZestConstants';

export default function JiraTable(props) {
    const [filteringText, setFilteringText] = React.useState("");
    const [collectionPreferences, setCollectionPreferences] = React.useState({
        visibleContent: [
            "id",
            "linked_issue",
            "project",
            "component",
            "summary",
            "patch_level",
            "actions"
        ],
        wrapLines: true,
        custom: "table"
    })
    const [filteredJiras, setFilteredJiras] = React.useState([]);
    React.useEffect(() => {
        setFilteredJiras(props.jiras);
    }, [props.jiras])

    // Helper function to determine the base URL based on filing system and stage
    const getBaseUrl = (filingSystem, environment) => {
        const isProd = environment.toLowerCase().includes('prod');
        
        if(filingSystem && filingSystem.toLowerCase() === 'shepherd') {
            return isProd ? ISSUE_URLS.SHEPHERD_PROD : ISSUE_URLS.SHEPHERD_BETA;
        } else {
            return isProd ? ISSUE_URLS.JIRA_PROD : ISSUE_URLS.JIRA_STAGING;
        }
    }

    const columnDefinitions = [
        {
          id: "id",
          header: "ID",
          cell: (item) => {
            try {
                if(item.error) {
                    return '--';
                }
                else {
                    return(
                        <ExternalLink
                            base={getBaseUrl(item.filing_system, item.environment)}
                            value={item.id}
                        />
                    );
                }
            }
            catch(err) {
              console.log(err);
            }
          }
        },
        {
            id: "linked_issue",
            header: "Linked Issue",
            cell: (item) => {
                if(item.linked_issue) {
                    // Linked issue uses the opposite filing system
                    const oppositeFilingSystem = item.filing_system && item.filing_system.toLowerCase() === 'shepherd' 
                        ? 'jira' 
                        : 'shepherd';
                    
                    return(
                        <ExternalLink
                            base={getBaseUrl(oppositeFilingSystem, item.environment)}
                            value={item.linked_issue}
                        />
                    );
                }
                return '--';
            }
        },
        {
            id: "component",
            header: "Component",
            cell: (item) => item.component ? item.component : '--'
        },
        {
            id: "summary",
            header: "Summary",
            cell: (item) => item.summary ? item.summary : '--'
        },
        {
            id: "patch_level",
            header: "Type",
            cell: (item) => <PatchLevelBadge patch_level={item.patch_level}/>
        }
    ]
    
    return (
          <Table
            loading={props.loading}
            items={filteredJiras}
            columnDefinitions={columnDefinitions}
            loadingText={'Loading Jiras...'}
            trackBy='id'
            filter={
                <TextFilter
                  filteringPlaceholder="Find Jira"
                  filteringText={filteringText}
                  onChange={({ detail }) => {
                        let filter = detail.filteringText.toLowerCase().trim();

                        if(!filter === null || filter.length === 0) {
                            setFilteredJiras(props.jiras);
                        }
                        else {
                            let tmp = [];
                            for(const jira of props.jiras) {
                                let summary = jira.summary ? jira.summary.toLowerCase() : '';
                                let project = jira.project ? jira.project.toLowerCase() : '';
                                let component = jira.component ? jira.component.toLowerCase() : '';
                                let id = jira.id ? jira.id.toLowerCase() : '';

                                if(summary.includes(filter)
                                    || project.includes(filter)
                                    || component.includes(filter)
                                    || id.includes(filter)) {
                                    tmp.push(jira);
                                }
                            }
                            setFilteredJiras(tmp);
                        }
                        
                        setFilteringText(filter);
                    }
                  }
                />
            }
            visibleColumns={collectionPreferences.visibleContent}
            empty={
              <div className="awsui-util-t-c">
                  <div className="awsui-util-pt-s awsui-util-mb-xs">
                      <b>No Jiras</b>
                  </div>
                  <p className="awsui-util-mb-s">
                      No Jiras filed for this job.
                  </p>
              </div>	
            }
            preferences={
                <CollectionPreferences
                    title="Preferences"
                    confirmLabel="Confirm"
                    cancelLabel="Cancel"
                    onConfirm={({ detail }) => {setCollectionPreferences(detail)}}
                    preferences={collectionPreferences}
                    visibleContentPreference={{
                        title: "Select visible columns",
                        options: [
                        {
                            label: "Fields",
                            options: [
                                {
                                    id: "id",
                                    label: "Jira ID",
                                    editable: false
                                },
                                { id: "linked_issue", label: "Linked Issue" },
                                { id: "project", label: "Project" },
                                { id: "component", label: "Component" },
                                { id: "summary", label: "Summary" },
                                { id: "patch_level", label: "Type" },
                                { id: "actions", label: "Actions" }
                            ]
                        }
                        ]
                    }}
                />
            }
          >
          </Table>
      );
}
